"use client";

import React, { useState } from 'react';
import CategoryFilter from '@/components/blog/CategoryFilter';
import FeaturedCard from '@/components/blog/FeaturedCard';
import PostCard from '@/components/blog/PostCard';
import { motion, AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';

const NewsletterSignup = dynamic(() => import('@/components/newsletter/NewsletterSignup'), { ssr: false });

interface BlogListingClientProps {
  initialPosts: any[];
  categories: any[];
  newsletterFormId: string;
}

export default function BlogListingClient({ initialPosts, categories, newsletterFormId }: BlogListingClientProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Filter posts based on active category
  const filteredPosts = activeCategory 
    ? initialPosts.filter(post => post.category?.slug?.current === activeCategory)
    : initialPosts;

  // Identify featured post (only for "All" view)
  const featuredPost = activeCategory === null 
    ? filteredPosts.find(post => post.featured) 
    : null;

  // Grid posts (excluding the featured one)
  const gridPosts = featuredPost 
    ? filteredPosts.filter(post => post.slug.current !== featuredPost.slug.current)
    : filteredPosts;

  return (
    <div className="container mx-auto px-6 pt-16 pb-24">
      {/* Category Pills */}
      <CategoryFilter 
        categories={categories} 
        activeCategory={activeCategory} 
        onSelect={setActiveCategory} 
      />

      {/* Featured Section */}
      <AnimatePresence mode="wait">
        {featuredPost && (
          <motion.div
            key="featured"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="mb-16"
          >
            <FeaturedCard post={featuredPost} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grid Section */}
      <motion.div 
        layout
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        <AnimatePresence mode="popLayout">
          {gridPosts.map((post) => (
            <motion.div
              layout
              key={post.slug.current}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <PostCard post={post} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Empty State */}
      {filteredPosts.length === 0 && (
        <div className="py-32 text-center">
          <p className="font-serif text-2xl text-bloom-muted italic">
            No articles found in this category yet. Check back soon!
          </p>
        </div>
      )}

      {/* Newsletter Section */}
      <section className="pb-24 pt-24 border-t border-bloom-border mt-24">
        <NewsletterSignup
          variant="inline"
          headline="Web insights for the organizations that matter"
          description="Practical guidance on accessibility, web design, and digital strategy. Published every Tuesday and Thursday."
          formId={newsletterFormId}
          tag="blog-subscriber"
        />
      </section>
    </div>
  );
}
